import brainImg from "../assets/stroke_and_damage_on_brain_finishedred+addedbackground1.webp";
import listImage1 from "../assets/1.1_Dark.svg";
import listImage2 from "../assets/Ellipse_2.svg";
import listImage3 from "../assets/Ellipse_1.svg";
import LazyImage from "../components/LazyImage";
import SEO from "../components/SEO";
import ScrollDepthTracker from "../components/ScrollDepthTracker";

export default function About() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Thrombio",
    "url": "https://www.thrombio.com.au/about",
    "mainEntity": {
      "@type": "Organization",
      "name": "Thrombio",
      "url": "https://www.thrombio.com.au/",
      "logo": "https://www.thrombio.com.au/thromBioInvert.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@thrombio.com.au"
      },
      "sameAs": [
        "https://www.linkedin.com/company/thrombioholdings"
      ]
    },
    "description": "Thrombio is a drug discovery company dedicated to advancing the global standard of care for the treatment of stroke using novel anti-thrombotic drugs with unprecedented safety and efficacy."
  };

  return (
    <>
      <SEO
        title="About Us"
        description="Discover ThromBio's mission to advance the global standard of care for the treatment of stroke"
        keywords={['Antithrombotic', 'Cardiovascular Disease', 'Medical Research', 'Drug Discovery', 'Stroke Research']} 
        ogImage={brainImg}
        schema={aboutSchema}
      />
      <ScrollDepthTracker pageCategory="about" pageName="index" />

      <div className="bg-[#010C0E]/10">
        <div className="pt-[338px] md:pt-[120px] xl:pt-[95px] pb-40 md:pb-[32px] aboutHeroBg relative">
          <div className="grid xl:grid-cols-3 xl:items-center md:w-max px-12 md:max-xl:px-[5rem] xl:page-width">
            <div className="flex flex-col gap-[3.2rem]">
              {/* Main heading with consistent font styling */}
              <div className="font-sans text-[4.8rem] font-bold text-white leading-[5.2rem] tracking-[.192rem]">
                <h1 className="sr-only">About ThromBio: Pioneering Stroke Treatment Innovations</h1>
                About Us
              </div>
              <p className="font-sans max-w-[542px] text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
                ThromBio is a drug discovery company focused on innovative antithrombotic treatments for cardiovascular
                disease.
                <br />
                <br />
                ThromBio's programme is driven by international leaders in drug discovery & development, experimental
                biomedicine global clinical trials capability and commercialisation.
              </p>
            </div>
            {/* Enhanced alt tag - this is above the fold so eager loading */}
            <LazyImage 
              src={brainImg} 
              alt="Blood clot in a brain vessel leading to brain damage diagram" 
              className="col-span-2 max-xl:hidden" 
              width={614}
              eager={true}
            />
          </div>
        </div>
        <div className="aboutMissionBg">
          <div className="mt-[15px] pt-24 md:pt-[290px] xl:page-width">
            <div className="flex flex-col gap-12 px-12 md:px-0">
              {/* Mission section heading with consistent styling */}
              <div className="font-bold text-[4.8rem] leading-[5.2rem] tracking-wide">
                <h1 className="sr-only">Our Mission: Revolutionizing Stroke Care</h1>
                Mission
              </div>
              <div className="font-syne font-bold text-[2.6rem] leading-[3.6rem]">
                Addressing An Unmet Clinical Need
              </div>
              <div className="font-sans text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
                ThromBio's mission is to advance the global standard of care for the treatment of stroke using novel
                anti-thrombotic drugs with unprecedented safety and efficacy.
              </div>
              <div className="flex flex-col gap-[18px]">
                <div className="flex items-start gap-[16px] md:gap-[44px]">
                  <LazyImage src={listImage1} alt="List bullet point" className="mt-2" eager={false} />
                  <div className="font-sans text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
                    ThromBio Holdings Pty Ltd stands at a pivotal juncture in the fight against thrombotic disorders.
                  </div>
                </div>
                <div className="flex items-start gap-[16px] md:gap-[44px]">
                  <LazyImage src={listImage1} alt="List bullet point" className="mt-2" eager={false} />
                  <div className="font-sans text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
                    The company is spearheading the development of novel therapeutic agents with the potential to
                    significantly advance the treatment of Acute Ischemic Stroke (AIS).
                  </div>
                </div>
                <div className="flex items-start gap-[16px] md:gap-[44px]">
                  <LazyImage src={listImage1} alt="List bullet point" className="mt-2" eager={false} />
                  <div className="font-sans text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
                    ThromBio is leveraging the groundbreaking scientific discoveries emanating from Professors Shaun
                    Jackson's laboratory.
                  </div>
                </div>
                <div className="flex items-start gap-[16px] md:gap-[44px]">
                  <LazyImage src={listImage1} alt="List bullet point" className="mt-2" eager={false} />
                  <div className="font-sans text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
                    ThromBio aims to transform the therapeutic landscape with its lead compounds which offer a unique
                    combination of efficacy and safety.
                  </div>
                </div>
              </div>
            </div>
            <div className="pt-[130px] xl:max-w-[907px] aboutImproveStrokeBg px-12 md:px-0">
              {/* Subheading with consistent font styling */}
              <div className="font-syne font-bold text-[2.4rem] leading-[3.6rem] tracking-[.12rem] max-md:text-center text-[#DA0912]">
                <h3 className="sr-only">Improving stroke therapy and patient outcomes</h3>
                Improving stroke therapy and patient outcomes
              </div>
              <div className="flex flex-col mt-[14px]">
                <div className="flex flex-col gap-4 md:gap-[30px] xl:gap-[50px] xl:pl-[56px]">
                  <div className="flex flex-col gap-4">
                    <div className="font-sans font-bold text-[2.2rem] leading-[3.4rem]">Market Urgency:</div>
                    <div className="flex items-start gap-[26px] md:gap-[56px] md:pl-[35px] xl:pl-0">
                      <LazyImage src={listImage2} alt="Blue bullet point" className="mt-2 md:hidden" eager={false} />
                      <div className="bg-[#C3EEFD] size-6 max-md:hidden rounded-full aspect-square mt-2"></div>
                      <div className="font-sans text-[1.85rem] leading-[2.7rem]">
                        Stroke remains a leading cause of death and disability worldwide, with limited effective
                        treatments available.
                      </div>
                    </div>
                    <div className="flex items-start gap-[26px] md:gap-[56px] md:pl-[35px] xl:pl-0">
                      <LazyImage src={listImage2} alt="Blue bullet point" className="mt-2 md:hidden" eager={false} />
                      <div className="bg-[#C3EEFD] size-6 max-md:hidden rounded-full aspect-square mt-2"></div>
                      <div className="font-sans text-[1.85rem] leading-[2.7rem]">
                        ThromBio's innovative approach addresses this substantial unmet need, representing a significant
                        market opportunity.
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="font-sans font-bold text-[2.2rem] leading-[3.4rem]">Innovative New Therapies:</div>
                    <div className="flex items-start gap-[26px] md:gap-[56px] md:pl-[35px] xl:pl-0">
                      <LazyImage src={listImage3} alt="Red bullet point" className="mt-2 md:hidden" eager={false} />
                      <div className="bg-[#DA0912] size-6 max-md:hidden rounded-full aspect-square mt-2"></div>
                      <div className="font-sans text-[1.85rem] leading-[2.7rem]">
                        TBO-309 has a novel mechanism of action, with the potential to extend treatment windows and
                        improve patient outcomes.
                      </div>
                    </div>
                    <div className="flex items-start gap-[26px] md:gap-[56px] md:pl-[35px] xl:pl-0">
                      <LazyImage src={listImage3} alt="Red bullet point" className="mt-2 md:hidden" eager={false} />
                      <div className="bg-[#DA0912] size-6 max-md:hidden rounded-full aspect-square mt-2"></div>
                      <div className="font-sans text-[1.85rem] leading-[2.7rem]">
                        TBO-309 has shown exceptional promise in enhancing thrombolysis without increasing bleeding
                        risk, potentially redefining acute stroke treatment.
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="font-sans font-bold text-[2.2rem] leading-[3.4rem]">Strategic Development Pathway:</div>
                    <div className="flex items-start gap-[26px] md:gap-[56px] md:pl-[35px] xl:pl-0">
                      <LazyImage src={listImage2} alt="Blue bullet point" className="mt-2 md:hidden" eager={false} />
                      <div className="bg-[#C3EEFD] size-6 max-md:hidden rounded-full aspect-square mt-2"></div>
                      <div className="font-sans text-[1.85rem] leading-[2.7rem]">
                        ThromBio has mapped out a clear and strategic development pathway, from clinical trials to
                        regulatory approval and commercialization.
                      </div>
                    </div>
                    <div className="flex items-start gap-[26px] md:gap-[56px] md:pl-[35px] xl:pl-0">
                      <LazyImage src={listImage2} alt="Blue bullet point" className="mt-2 md:hidden" eager={false} />
                      <div className="bg-[#C3EEFD] size-6 max-md:hidden rounded-full aspect-square mt-2"></div>
                      <div className="font-sans text-[1.85rem] leading-[2.7rem]">
                        A major objective for ThromBio is to maximise the therapeutic and commercial potential of its
                        pipeline.
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="font-sans font-bold text-[2.2rem] leading-[3.4rem]">Global Market Potential:</div>
                    <div className="flex items-start gap-[26px] md:gap-[56px] md:pl-[35px] xl:pl-0">
                      <LazyImage src={listImage3} alt="Red bullet point" className="mt-2 md:hidden" eager={false} />
                      <div className="bg-[#DA0912] size-6 max-md:hidden rounded-full aspect-square mt-2"></div>
                      <div className="font-sans text-[1.85rem] leading-[2.7rem]">
                        The global stroke market is expected to experience robust growth in the coming decades.
                      </div>
                    </div>
                    <div className="flex items-start gap-[26px] md:gap-[56px] md:pl-[35px] xl:pl-0">
                      <LazyImage src={listImage3} alt="Red bullet point" className="mt-2 md:hidden" eager={false} />
                      <div className="bg-[#DA0912] size-6 max-md:hidden rounded-full aspect-square mt-2"></div>
                      <div className="font-sans text-[1.85rem] leading-[2.7rem]">
                        ThromBio's targeted therapies are well-positioned to capture a significant share of the global
                        stroke market, particularly in key markets with high AIS incidence.
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="font-sans font-bold text-[2.2rem] leading-[3.4rem]">Experienced Leadership:</div>
                    <div className="flex items-start gap-[26px] md:gap-[56px] md:pl-[35px] xl:pl-0">
                      <LazyImage src={listImage2} alt="Blue bullet point" className="mt-2 md:hidden" eager={false} />
                      <div className="bg-[#C3EEFD] size-6 max-md:hidden rounded-full aspect-square mt-2"></div>
                      <div className="font-sans text-[1.85rem] leading-[2.7rem]">
                        The company benefits from the strategic guidance of an accomplished Board of Directors,
                        management team and scientific and clinical advisory board, ensuring a focused and effective
                        approach to development and commercialisation.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}